import React, { useCallback, useEffect, useState } from "react";
import api, { API_URL, BULAN, formatApiError, formatRupiah, getToken, SPP_KOMPONEN, SPP_METODE, SPP_PENERIMA, SPP_TAHUN , formatTanggal} from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_BENDAHARA } from "@/lib/nav";
import { FilterBar } from "@/components/FilterBar";
import { SearchableSelect } from "@/components/SearchableSelect";
import BackupExcelButton from "@/components/BackupExcelButton";
import { Plus, Pencil, Trash2, Download, FileText, Eye, Upload, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

function SppExcelControls({ onReload }) {
  const [busy, setBusy] = useState(false);
  const downloadTemplate = () => {
    const t = getToken();
    const url = `${API_URL}/excel/spp/template${t ? `?auth=${encodeURIComponent(t)}` : ""}`;
    window.open(url, "_blank");
  };
  const handleUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/excel/spp/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Berhasil import: ${data.inserted} baris`);
      if (data.errors?.length) toast.error(`Error (${data.errors.length}): ${data.errors.slice(0, 3).join(" | ")}`);
      onReload?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <>
      <button onClick={downloadTemplate} className="pp-btn-secondary" data-testid="spp-dl-template">
        <FileSpreadsheet className="w-4 h-4" /> Template Excel
      </button>
      <label className="pp-btn-secondary cursor-pointer" data-testid="spp-upload-label">
        <Upload className="w-4 h-4" /> {busy ? "Mengimport…" : "Upload Excel (Massal)"}
        <input type="file" accept=".xlsx" onChange={handleUpload} className="hidden" data-testid="spp-upload-input" />
      </label>
    </>
  );
}

const F_EMPTY = { q: "", komponen: "", bulan: "", tahun: "", metode: "", penerima: "", tanggal_from: "", tanggal_to: "" };

export function BendaharaSPP({ nav = NAV_BENDAHARA, canEdit = true, downloadOnly = false, showDownload = true, pageTitle = "Data Pembayaran SPP", extraTopAction = null }) {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState(F_EMPTY);
  const [applied, setApplied] = useState(F_EMPTY);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const params = {};
      if (applied.bulan) params.bulan = applied.bulan;
      if (applied.tahun) params.tahun = applied.tahun;
      if (applied.metode) params.metode = applied.metode;
      if (applied.penerima) params.penerima = applied.penerima;
      if (applied.komponen) params.komponen = applied.komponen;
      if (applied.tanggal_from) params.tanggal_from = applied.tanggal_from;
      if (applied.tanggal_to) params.tanggal_to = applied.tanggal_to;
      const { data } = await api.get("/spp", { params });
      const filtered = applied.q
        ? data.filter((d) => (d.nama_siswa || "").toLowerCase().includes(applied.q.toLowerCase()))
        : data;
      setItems(filtered);
    } catch (e) { console.error(e); }
  }, [applied]);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Hapus data SPP ini?")) return;
    setBusy(true);
    try { await api.delete(`/spp/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal hapus"); }
    finally { setBusy(false); }
  };

  const kwitansiUrl = (id) => {
    const t = getToken();
    return `${API_URL}/spp/${id}/kwitansi.pdf${t ? `?auth=${encodeURIComponent(t)}` : ""}`;
  };

  const downloadHref = () => {
    const t = getToken();
    const params = new URLSearchParams();
    if (applied.bulan) params.append("bulan", applied.bulan);
    if (applied.tahun) params.append("tahun", applied.tahun);
    if (applied.tanggal_from) params.append("tanggal_from", applied.tanggal_from);
    if (applied.tanggal_to) params.append("tanggal_to", applied.tanggal_to);
    if (t) params.append("auth", t);
    const qs = params.toString();
    return `${API_URL}/export/spp${qs ? `?${qs}` : ""}`;
  };

  return (
    <Layout nav={nav}>
      <PageTitle title={pageTitle} action={
        <div className="flex flex-wrap gap-2">
          {extraTopAction}
          <BackupExcelButton endpoint="spp" filters={{ bulan: applied.bulan, tahun: applied.tahun, nama: applied.q, metode: applied.metode, penerima: applied.penerima, komponen: applied.komponen, tanggal_from: applied.tanggal_from, tanggal_to: applied.tanggal_to }} testId="backup-spp-btn" />
          {showDownload && (
            <a href={downloadHref()} target="_blank" rel="noreferrer" className="pp-btn-secondary" data-testid="spp-download">
              <Download className="w-4 h-4" /> Download PDF
            </a>
          )}
          {canEdit && !downloadOnly && (
            <>
              <SppExcelControls onReload={load} />
              <button onClick={() => navigate("/bendahara/spp/input")} className="pp-btn-primary" data-testid="spp-input-open">
                <Plus className="w-4 h-4" /> Input SPP
              </button>
            </>
          )}
        </div>
      } />

      <FilterBar
        filters={filters} setFilters={setFilters}
        onApply={() => setApplied({ ...filters })}
        onReset={() => { setFilters(F_EMPTY); setApplied(F_EMPTY); }}
        dataTestidPrefix="spp-filter"
        fields={[
          { key: "q", type: "text", label: "Nama Siswa", placeholder: "Cari nama siswa" },
          { key: "komponen", type: "select", label: "Pembayaran", options: SPP_KOMPONEN.map((k) => ({ v: k.v, label: k.label })) },
          { key: "bulan", type: "bulan", label: "Bulan" },
          { key: "tahun", type: "select", label: "Tahun", options: SPP_TAHUN.map(String) },
          { key: "tanggal_from", type: "tanggal", label: "Tgl Bayar Dari" },
          { key: "tanggal_to", type: "tanggal", label: "Tgl Bayar Sampai" },
          { key: "metode", type: "select", label: "Metode", options: SPP_METODE.map((m) => ({ v: m.v, label: m.label })) },
          ...(canEdit ? [{ key: "penerima", type: "select", label: "Penerima", options: SPP_PENERIMA }] : []),
        ]}
      />

      <section className="pp-card pp-freeze overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1200px] w-full text-sm" data-testid="spp-table">
            <thead>
              <tr className="pp-table-header">
                <th className="text-left px-4 py-3 sticky left-0 bg-indigo-600 z-10 min-w-[180px] text-white">Nama</th>
                <th className="text-right px-4 py-3">Pendaftaran</th>
                <th className="text-right px-4 py-3">Pangkal</th>
                <th className="text-right px-4 py-3">SPP</th>
                <th className="text-right px-4 py-3">Seragam</th>
                <th className="text-left px-4 py-3">Bulan</th>
                <th className="text-left px-4 py-3">Tahun</th>
                <th className="text-right px-4 py-3">Jlh Bayar</th>
                <th className="text-left px-4 py-3">Tgl Bayar</th>
                <th className="text-left px-4 py-3">Melalui</th>
                {canEdit && <th className="text-left px-4 py-3">Penerima</th>}
                <th className="text-left px-4 py-3">Bukti</th>
                {canEdit && !downloadOnly && <th className="text-right px-4 py-3">Aksi</th>}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={canEdit ? 13 : 11} className="text-center py-10 text-slate-400 italic">Belum ada data.</td></tr>
              )}
              {items.map((it) => (
                <tr key={it.id} className="pp-row" data-testid={`spp-row-${it.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900 sticky left-0 bg-white z-10">{it.nama_siswa}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs">{it.pendaftaran ? formatRupiah(it.pendaftaran) : "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs">{it.pangkal ? formatRupiah(it.pangkal) : "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs">{it.spp ? formatRupiah(it.spp) : "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs">{it.seragam ? formatRupiah(it.seragam) : "—"}</td>
                  <td className="px-4 py-3">{BULAN.find((b) => b.v === it.bulan)?.label}</td>
                  <td className="px-4 py-3">{it.tahun}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{formatRupiah(it.total)}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{formatTanggal(it.tanggal_bayar)}</td>
                  <td className="px-4 py-3 capitalize text-xs">{it.metode}</td>
                  {canEdit && <td className="px-4 py-3 text-xs">{it.penerima}</td>}
                  <td className="px-4 py-3">
                    <a href={kwitansiUrl(it.id)} target="_blank" rel="noreferrer" className="text-indigo-600 inline-flex items-center gap-1 text-xs" data-testid={`spp-kwitansi-${it.id}`}>
                      <FileText className="w-3.5 h-3.5" /> Lihat
                    </a>
                  </td>
                  {canEdit && !downloadOnly && (
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => navigate(`/bendahara/spp/input?edit=${it.id}`)} className="pp-btn-ghost text-xs" data-testid={`spp-edit-${it.id}`}>
                          <Pencil className="w-3.5 h-3.5" /> Edit
                        </button>
                        <button disabled={busy} onClick={() => del(it.id)} className="pp-btn-danger" data-testid={`spp-del-${it.id}`}>
                          <Trash2 className="w-3.5 h-3.5" /> Hapus
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </Layout>
  );
}

// ---------------- INPUT SPP PAGE ----------------
export function BendaharaInputSPP() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const editId = params.get("edit");

  const [siswa, setSiswa] = useState([]);
  const [form, setForm] = useState({
    siswa_id: "", bulan: String(new Date().getMonth() + 1).padStart(2, "0"), tahun: new Date().getFullYear(),
    pendaftaran: "", pangkal: "", spp: "", seragam: "",
    metode: "tunai", penerima: "",
    tanggal_bayar: new Date().toISOString().slice(0, 10), catatan: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/siswa", { params: { status: "aktif" } });
      setSiswa(data);
      if (editId) {
        const { data: list } = await api.get("/spp");
        const it = list.find((x) => x.id === editId);
        if (it) {
          setForm({
            siswa_id: it.siswa_id, bulan: it.bulan, tahun: it.tahun,
            pendaftaran: it.pendaftaran || "", pangkal: it.pangkal || "", spp: it.spp || "", seragam: it.seragam || "",
            metode: it.metode, penerima: it.penerima,
            tanggal_bayar: it.tanggal_bayar, catatan: it.catatan || "",
          });
        }
      }
    })();
  }, [editId]);

  const total = ["pendaftaran", "pangkal", "spp", "seragam"].reduce((a, k) => a + (parseFloat(form[k]) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.siswa_id) return toast.error("Pilih siswa terlebih dahulu");
    if (!form.penerima) return toast.error("Pilih penerima terlebih dahulu");
    if (total <= 0) return toast.error("Total pembayaran harus lebih dari 0");
    setSaving(true);
    try {
      const payload = {
        ...form,
        tahun: parseInt(form.tahun, 10),
        pendaftaran: parseFloat(form.pendaftaran) || 0,
        pangkal: parseFloat(form.pangkal) || 0,
        spp: parseFloat(form.spp) || 0,
        seragam: parseFloat(form.seragam) || 0,
      };
      if (editId) await api.put(`/spp/${editId}`, payload);
      else await api.post("/spp", payload);
      toast.success("Pembayaran SPP tersimpan");
      navigate("/bendahara/spp");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  return (
    <Layout nav={NAV_BENDAHARA}>
      <PageTitle title={editId ? "Edit Pembayaran SPP" : "Input Pembayaran SPP"} />
      <section className="pp-card p-6 max-w-3xl">
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="spp-input-form">
          <div className="sm:col-span-2"><label className="pp-label">Nama Siswa *</label>
            <SearchableSelect
              options={siswa.map((s) => ({ value: s.id, label: `${s.nama}${s.nis ? ` — ${s.nis}` : ""}` }))}
              value={form.siswa_id}
              onChange={(v) => setForm({ ...form, siswa_id: v })}
              placeholder="— Ketik nama siswa untuk mencari —"
              testId="spp-in-siswa"
            />
          </div>
          <div><label className="pp-label">Total Pembayaran</label>
            <input readOnly className="pp-input bg-slate-50 font-semibold text-indigo-700" value={formatRupiah(total)} data-testid="spp-in-total" />
          </div>
          <div><label className="pp-label">Metode Pembayaran *</label>
            <select required className="pp-input" value={form.metode} onChange={(e) => setForm({ ...form, metode: e.target.value })} data-testid="spp-in-metode">
              {SPP_METODE.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
            </select>
          </div>
          <div><label className="pp-label">Pendaftaran (Rp)</label>
            <input type="number" min={0} className="pp-input" value={form.pendaftaran} onChange={(e) => setForm({ ...form, pendaftaran: e.target.value })} data-testid="spp-in-pendaftaran" />
          </div>
          <div><label className="pp-label">Uang Pangkal (Rp)</label>
            <input type="number" min={0} className="pp-input" value={form.pangkal} onChange={(e) => setForm({ ...form, pangkal: e.target.value })} data-testid="spp-in-pangkal" />
          </div>
          <div><label className="pp-label">SPP Bulan *</label>
            <select className="pp-input" value={form.bulan} onChange={(e) => setForm({ ...form, bulan: e.target.value })} data-testid="spp-in-bulan">
              {BULAN.map((b) => <option key={b.v} value={b.v}>{b.label}</option>)}
            </select>
          </div>
          <div><label className="pp-label">SPP (Rp)</label>
            <input type="number" min={0} className="pp-input" value={form.spp} onChange={(e) => setForm({ ...form, spp: e.target.value })} data-testid="spp-in-spp" />
          </div>
          <div><label className="pp-label">Seragam (Rp)</label>
            <input type="number" min={0} className="pp-input" value={form.seragam} onChange={(e) => setForm({ ...form, seragam: e.target.value })} data-testid="spp-in-seragam" />
          </div>
          <div><label className="pp-label">Diterima Oleh *</label>
            <select required className="pp-input" value={form.penerima} onChange={(e) => setForm({ ...form, penerima: e.target.value })} data-testid="spp-in-penerima">
              <option value="">— Pilih Penerima —</option>
              {SPP_PENERIMA.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div><label className="pp-label">Tanggal Bayar *</label>
            <input required type="date" className="pp-input" value={form.tanggal_bayar} onChange={(e) => setForm({ ...form, tanggal_bayar: e.target.value })} data-testid="spp-in-tgl" />
          </div>
          <div><label className="pp-label">Tahun *</label>
            <select className="pp-input" value={form.tahun} onChange={(e) => setForm({ ...form, tahun: e.target.value })} data-testid="spp-in-tahun">
              {SPP_TAHUN.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2"><label className="pp-label">Catatan</label>
            <textarea rows={2} className="pp-input" value={form.catatan} onChange={(e) => setForm({ ...form, catatan: e.target.value })} data-testid="spp-in-catatan" />
          </div>
          <div className="sm:col-span-2 flex justify-end gap-2 pt-4 border-t border-slate-200">
            <button type="button" onClick={() => navigate("/bendahara/spp")} className="pp-btn-secondary">Batal</button>
            <button type="submit" disabled={saving} className="pp-btn-primary" data-testid="spp-in-submit">
              {saving ? "Menyimpan…" : "Simpan SPP"}
            </button>
          </div>
        </form>
      </section>
    </Layout>
  );
}

// ---------------- Bendahara Profil (read-only, edited by admin) ----------------
export function BendaharaProfil() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data: me } = await api.get("/auth/me");
        setData(me);
      } catch (e) { console.error(e); }
    })();
  }, []);
  if (!data) return <Layout nav={NAV_BENDAHARA}><PageTitle title="Profil Bendahara" /><div className="pp-card p-12 text-center text-slate-400 italic">Memuat…</div></Layout>;
  return (
    <Layout nav={NAV_BENDAHARA}>
      <PageTitle title="Profil Bendahara" />
      <section className="pp-card p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row gap-6 mb-6">
          {data.foto_file_id ? (
            <img src={`${API_URL}/files/${data.foto_file_id}/download${getToken() ? `?auth=${encodeURIComponent(getToken())}` : ""}`} className="w-36 h-36 rounded-xl object-cover border border-slate-200" alt="" />
          ) : (
            <div className="w-36 h-36 rounded-xl bg-slate-100 flex items-center justify-center text-slate-300 text-5xl font-bold">{(data.nama || "?")[0]}</div>
          )}
          <div className="flex-1">
            <h3 className="text-2xl font-bold">{data.nama || "—"}</h3>
            <p className="text-slate-500">Bendahara</p>
            <p className="text-xs text-slate-400 mt-2 italic">Profil hanya bisa diedit oleh Admin.</p>
          </div>
        </div>
        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm pt-6 border-t border-slate-100">
          {[
            ["NIY", data.niy || "—"],
            ["Tempat/Tgl Lahir", `${data.tempat_lahir || "—"}, ${formatTanggal(data.tanggal_lahir)}`],
            ["No. Telp", data.telp || "—"],
            ["Alamat", data.alamat || "—"],
          ].map(([k, v]) => (<div key={k}><dt className="text-xs uppercase text-slate-500">{k}</dt><dd className="text-slate-900 mt-0.5">{v}</dd></div>))}
        </dl>
      </section>
    </Layout>
  );
}

// Icons used only in dashboard - re-exported so callers can import a single file
export { Eye };
